/**
 * One review finding, wherever it appears (YAG-424).
 *
 * A finding used to render twice with no shared vocabulary: the report modal
 * gave it a toned `Chip` and a tail-kept path, the Work hold gave it a bare
 * `span` with no tone and a `file · L42`. Same object, two class families, two
 * severity treatments, two path formats. This is the one shape.
 *
 * Severity tones come from the material language, not from a per-surface
 * decision: blue accent asks for a human decision, honesty amber marks a risk,
 * advise stays neutral, a nit is barely material. Severity is the producer's
 * free text, so anything unrecognised reads neutral rather than guessing an
 * importance it was not given.
 *
 * `mark` is the one-line footnote slot and holds at most one voice: the
 * editor's reason for not carrying it, a verification verdict, or a follow-up
 * outcome. `actions` is filled only where a person may actually act.
 *
 * @example
 * <Finding severity="risk" file="src/notify/twilio.ts" line={34} body="A new client per send." />
 * <Finding severity="nit" body="Reads oddly." mark="not carried · judged speculative" dimmed />
 */
import type { ReactNode } from "react";
import { Chip } from "./chip";
import "./finding.css";

export type FindingSeverityTone =
  | "neutral"
  | "accent"
  | "success"
  | "honesty"
  | "incident"
  | "dashed";

/** The one severity scale. Both former call sites now read this. */
export const SEVERITY_CHIP_TONE: Record<string, FindingSeverityTone> = {
  decision: "accent",
  risk: "honesty",
  advise: "neutral",
  nit: "dashed",
};

export function severityChipTone(severity: string): FindingSeverityTone {
  return SEVERITY_CHIP_TONE[severity.toLowerCase()] ?? "neutral";
}

/**
 * The confidence scale (YAG-435): how sure the producer is, distinct from
 * how bad the finding would be. Low wears the dashed (not-yet-established)
 * tone; high earns success; an unrecognized value reads neutral, and an
 * ABSENT one renders nothing — older findings never invented a confidence.
 */
export const CONFIDENCE_CHIP_TONE: Record<string, FindingSeverityTone> = {
  high: "success",
  medium: "neutral",
  low: "dashed",
};

export function confidenceChipTone(confidence: string): FindingSeverityTone {
  return CONFIDENCE_CHIP_TONE[confidence.toLowerCase()] ?? "neutral";
}

/**
 * A finding's location, one line, tail kept: the filename and line survive
 * whole and whole path segments drop from the LEFT until what remains fits.
 * Never the CSS `direction: rtl` ellipsis trick, which bidi-reorders digits
 * and the `:line` suffix.
 */
export function truncatedPathLabel(
  file: string,
  line: number | null,
  budget = 48,
): string {
  const suffix = line !== null ? `:${line}` : "";
  if (file.length + suffix.length <= budget) return `${file}${suffix}`;
  const segments = file.split("/");
  const kept: string[] = [];
  // 2 = the "…/" prefix; each segment costs itself plus its separator.
  let length = 2 + suffix.length;
  for (let i = segments.length - 1; i >= 0; i--) {
    const cost = segments[i]!.length + (kept.length > 0 ? 1 : 0);
    if (kept.length > 0 && length + cost > budget) break;
    kept.unshift(segments[i]!);
    length += cost;
  }
  return `…/${kept.join("/")}${suffix}`;
}

/**
 * Prose with backtick spans rendered as code — the one markdown-ism a finding
 * body gets. Deliberately not the full Markdown component: a finding body is a
 * sentence, and surprise links or lists have no place in it.
 */
function TextWithCode({ text }: { text: string }) {
  const parts = text.split(/(`[^`\n]+`)/g);
  return (
    <>
      {parts.map((part, index) =>
        part.length > 2 && part.startsWith("`") && part.endsWith("`") ? (
          <code key={index}>{part.slice(1, -1)}</code>
        ) : (
          part
        ),
      )}
    </>
  );
}

export interface FindingProps {
  /** The producer's free text. Absent on findings that carry none. */
  severity?: string | null;
  /** The check or role that raised it, when the surface knows. */
  check?: string | null;
  /** "high" | "medium" | "low" (YAG-435). Absent = producer predates it. */
  confidence?: string | null;
  file?: string | null;
  line?: number | null;
  body: string;
  /** A code excerpt, where the producer captured one. */
  snippet?: string | null;
  /**
   * One footnote line: why it was not carried, a verification verdict, a
   * follow-up outcome. At most one, because a finding wearing three marks
   * reads as an argument with itself.
   */
  mark?: ReactNode;
  /** Keep / edit / remove, only where a person may act. */
  actions?: ReactNode;
  /** Struck and faded: dropped by the editor, or refuted in verification. */
  dimmed?: boolean;
  /** Replaces the body when a person is rewriting it. */
  children?: ReactNode;
  testId?: string;
}

export function Finding({
  severity,
  check,
  confidence,
  file,
  line,
  body,
  snippet,
  mark,
  actions,
  dimmed = false,
  children,
  testId,
}: FindingProps) {
  const location = file ? truncatedPathLabel(file, line ?? null) : null;
  return (
    <li
      className={`ui-finding${dimmed ? " is-dimmed" : ""}`}
      data-testid={testId}
      data-severity={severity ?? undefined}
      data-confidence={confidence ?? undefined}
    >
      {(severity || check || confidence) && (
        <span className="ui-finding-meta">
          {severity ? <Chip tone={severityChipTone(severity)}>{severity}</Chip> : null}
          {check ? <Chip tone="neutral">{check}</Chip> : null}
          {confidence ? (
            <Chip tone={confidenceChipTone(confidence)}>{`${confidence} confidence`}</Chip>
          ) : null}
        </span>
      )}
      {/* The location is its own line above the body, never in the chip
          column: a path is wider than any rail, and inside one it ran over
          the text it locates. */}
      {location ? (
        <span className="ui-finding-loc" title={file ?? undefined}>
          {location}
        </span>
      ) : null}
      {children ?? (
        <span className="ui-finding-body">
          <TextWithCode text={body} />
        </span>
      )}
      {snippet ? <pre className="ui-finding-snippet">{snippet}</pre> : null}
      {mark ? <span className="ui-finding-mark">{mark}</span> : null}
      {actions ? <span className="ui-finding-actions">{actions}</span> : null}
    </li>
  );
}
